import { Table, Button, Input, Label, Alert, Modal, ModalBody, ModalFooter, ModalHeader, FormGroup } from "reactstrap";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import { DASHBOARD_ROOT } from "../../components/Routes/routingConstants";
import { useUserQuestionnairesAnswers } from "../../hooks/useUserQuestionnaireAnswers";
import { addUserQuestionnairesAnswers, editUserQuestionnairesAnswers, removeUserQuestionnairesAnswers } from "../../graphql/content-management/addUserQuestionnaireAnswers";
import { UserQuestionnairesAnswer, UserQuestionnairesAnswerLabels } from "../../interfaces/models/UserQuestionnaireAnswer";
import { useEffect, useRef, useState } from "react";
import { FilterTimerSearch } from "../../helper/searchHelper";
import Loading from "../../components/Loading";
import FormError from "../../components/Shared/Forms/FormError";
import { useMutation } from '@apollo/client';
import toast from "react-hot-toast";
import PromptDialog from "../../components/PromptDialog";

//#region Interfaces
interface ModalProperties {
    name : string;
    buttonName : string;
}
//#endregion Interfaces

const UserQuestionnairesAnswersPage = () => {
    
//#region Initialization
    const breadcrumbsData = [
        { name: 'Content List', path: `${DASHBOARD_ROOT}/content-management/` },
        { name: 'Edit Questionnaires Answers', path: `${DASHBOARD_ROOT}/content-management/questionnaires-answers` },
    ];
    
    const { data, loading, error } = useUserQuestionnairesAnswers();
    const [addUserQuestionnairesAnswerHook] = useMutation(addUserQuestionnairesAnswers,{
        onCompleted : () =>{
            toast.success("Questionnaire Answer was successfully added. Please wait page is Reloading");
        },
        onError : (err) =>{
            toast.error(err.graphQLErrors[0].message);
        }
    });
    const [editUserQuestionnairesAnswerHook] = useMutation(editUserQuestionnairesAnswers,{
        onCompleted : () =>{
            toast.success("Questionnaire Answer was successfully updated. Please wait page is Reloading");
        },
        onError : (err) =>{
            toast.error(err.graphQLErrors[0].message);
        }
    });

    const [removeUserQuestionnairesAnswerHook] = useMutation(removeUserQuestionnairesAnswers,{
        onCompleted : () =>{
            toast.success("Questionnaire Answer was successfully removed. Please wait page is Reloading");
        },
        onError : (err) =>{
            toast.error(err.message);
        }
    });

    const [answers, setAnswers] = useState<UserQuestionnairesAnswer[]>([]);
    const [answersFiltered, setAnswersFiltered] = useState<UserQuestionnairesAnswer[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);

    let labelIndex = useRef<number> (0);
    const [selectedItem, setSelectedItem] = useState<string>("");
    const [newAnswer, setNewAnswer] = useState<string>("");
    const [selectedItemLabels, setSelectedItemLabels] = useState<UserQuestionnairesAnswerLabels[]>([]);
    const [selectedAnswer, setSelectedAnswer] = useState<UserQuestionnairesAnswer>(null);
    const [modal, setModal] = useState<ModalProperties>({
        name : "",
        buttonName : ""
    });

    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);

    const [isOpenDelete, setIsOpenDelete] = useState<boolean>(false);

    const Reloader = () => {
        setTimeout(() => {
            window.location.reload();
        },1000)
    }

    const HandleSearch = (search : string) => {
        search = search?.trim().toLowerCase();
        FilterTimerSearch(setIsSearching, () => {
            let filtered = answers.filter((answer) => {
                return answer.name.toLowerCase().includes(search.toLowerCase());
            });
            setAnswersFiltered(filtered);
        });
    }

    useEffect(() => {
        if(data) {
            setAnswers(data.user_questionnaire_answers);
            setAnswersFiltered(data.user_questionnaire_answers);
        }
    },[data]);
//#endregion Initialization
//#region Add and Update Functions
    const ToggleAddModal = () => {
        setIsAddModalOpen(!isAddModalOpen);
    }

    const HandleOpenAddModal = () => {
        setSelectedItem("");
        setSelectedItemLabels([]);
        setModal({
            name : "Add Answer Set",
            buttonName : "Add"
        });
        labelIndex.current = 0;
        setIsAddModalOpen(true);
    }

    const HandleAddSubmit = async () => {
        if(!selectedItem) {
            return;
        }

        if(selectedItemLabels.length === 0) {
            toast.error("Please Add Options for the answer set");
            return;
        }

        setIsProcessing(true);

        let isProcessed = false;
        

        if(modal.buttonName === "Add") {
            isProcessed = await addUserQuestionnairesAnswerHook({
                variables : {
                    name : selectedItem,
                    answers : selectedItemLabels
                }
            }).then((res) => res.errors ? false : true).catch(()=>false);
            
        }
        else { 
            isProcessed = await editUserQuestionnairesAnswerHook({
                variables : {
                    id : selectedAnswer._id,
                    name : selectedItem,
                    answers : selectedItemLabels
                }
            }).then((res) => res.errors ? false : true).catch(()=>false);
        }


        if(!isProcessed) {
            setIsProcessing(false);
            return;
        }

        Reloader();
    }

    const HandleOpenUpdateModal = (item : UserQuestionnairesAnswer) => {
        setSelectedAnswer(item);
        setSelectedItem(item.name);
        setModal({
            name : "Edit Answer Set",
            buttonName : "Edit"
        });
        setIsAddModalOpen(true);
        labelIndex.current = item.answers.length;
        setSelectedItemLabels(JSON.parse(JSON.stringify(item.answers)));
    }
//#endregion Add and Update Functions
//#region Delete Functions
    const HandleOpenDelete = (item : UserQuestionnairesAnswer) => {
        setSelectedAnswer(item);
        setIsOpenDelete(true);
    }

    const HandleDeleteSubmit =  async () => {
        setIsProcessing(true);

        const isDeleted = await removeUserQuestionnairesAnswerHook({
            variables : {
                _id : selectedAnswer._id
            }
        }).then((res) => res.errors ? false : true).catch(()=>false);

        if(!isDeleted) {
            setIsProcessing(false);
            return;
        }

        Reloader();
    }
//#endregion Delete Functions
//#region Form Functions
    const AddNewAnswer = () => {
        if(!newAnswer || newAnswer?.length > 30) {
            return;
        }
        setNewAnswer("");
        setSelectedItemLabels([...selectedItemLabels,  { _id : (++labelIndex.current).toString(), label : newAnswer }]);
    }

    const RemoveAnswer = (id : string) => {
        let newOptions = [...selectedItemLabels];
        const getIndex = newOptions.findIndex(u => u._id === id);
        newOptions.splice(getIndex,1);
        setSelectedItemLabels(newOptions);
    }

    const HandleAnswerChange = (index : number, value : string) => {
        const newOptions = [...selectedItemLabels];
        newOptions[index].label = value;
        setSelectedItemLabels(newOptions);
    };
//#endregion Form Functions

    return (
        <>
            <Breadcrumbs 
                crumbs={breadcrumbsData}
            />
            {
                error && <Alert color="danger">An error occurred while fetching questionnaires answers</Alert>
            }
            <div className="d-flex justify-content-end mb-2">
                <Button color="primary" onClick={HandleOpenAddModal}>Add</Button>
            </div>
            <div className="d-flex align-items-center mb-3">
                <Label className="mr-2">Search: </Label>
                <Input type="text" placeholder="Search Questionnaire Answers Name Here" onKeyUp={(e) => { HandleSearch(e.currentTarget.value)}} />
            </div>
            <div className="table-responsive  table-max-height">
                <Table bordered className=" bg-white">
                    <thead className="sticky-top bg-white">
                        <tr>
                            <th>Questionnaire Answer Set Name</th>
                            <th>Actions</th>
                        </tr>
                    </thead>

                    <tbody>
                        {
                            !loading && !isSearching && answersFiltered.length === 0 &&
                            <tr className="text-center">
                                <td colSpan={2}>No Questionnaires Answers Found</td>
                            </tr>
                        }
                        {
                            loading || isSearching 
                            ? 
                                <tr>
                                    <td colSpan={2} className="text-center"><Loading /></td>
                                </tr> 
                            :
                                answersFiltered.map((answer) => {
                                    return (
                                        <tr key={answer._id}>
                                            <td>{answer.name}</td>
                                            <td>
                                                <Button color="primary" size="sm" onClick={() => { HandleOpenUpdateModal(answer) }}>Edit</Button>
                                                <Button color="danger" size="sm" onClick={() => { HandleOpenDelete(answer) }}>Delete</Button>
                                            </td>
                                        </tr>
                                    )
                                })
                        }
                    </tbody>
                </Table>
            </div>

            <Modal
                id="questionnaire-add-modal"
                backdrop={true}
                isOpen={isAddModalOpen}
                className="modal-lg"
            >
                <ModalHeader toggle={ToggleAddModal} className="pb-0">
                    <p className="text-lg">{modal.name}</p>
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>Answer Set Name *</Label>
                        <Input type="text" value={selectedItem} onChange={(e) => { setSelectedItem(e.currentTarget.value); }}  placeholder="Ex. Scale of 1-5"/>
                        {
                            selectedItem.length === 0 && 
                            <FormError>Required</FormError>
                        }
                    </FormGroup>
                    
                    <Label>Add Option</Label>
                    {
                        selectedItemLabels.map((item, key) => 
                            <div className="d-flex justify-content-between mt-3" key={key}>
                                <div className=" w-100" >
                                    <Input type="text" value={item.label} onChange={(e) => { HandleAnswerChange(key, e.currentTarget.value) }} className="" />
                                </div>
                                <div>
                                    <Button className="ml-5" color="danger" onClick={()=>{ RemoveAnswer(item._id) }}> Remove</Button>
                                </div>
                            </div>
                        )
                    }
                    <FormGroup>
                        <div className="d-flex mt-3">
                            <Input type="text" value={newAnswer} onChange={(e) => { setNewAnswer(e.currentTarget.value) }} placeholder="Type here Ex. Excellent, Good, Average Then Click Add"/>
                            <Button color="secondary" className="ml-5" onClick={AddNewAnswer}>Add</Button>
                        </div>
                    </FormGroup>
                        
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" disabled={isProcessing} onClick={ToggleAddModal} >Close </Button>
                    <Button color="primary" disabled={isProcessing} onClick={HandleAddSubmit} >{modal.buttonName}</Button>
                </ModalFooter>
            </Modal>

            <PromptDialog
                isOpen={isOpenDelete}
                onCancel={() => { setIsOpenDelete(false); }}
                onConfirm={HandleDeleteSubmit}
                title="Delete Answer Set"
                promptMessage="Are you sure you want to delete this Answer Set?"
                confirmBtnTxt="Delete"
                isOnProcess={isProcessing}
            />
                        
        </>
    )
}

export { UserQuestionnairesAnswersPage as UserQuestionnairesAnswers };